import { apiClient } from "@/http";
import { apiRoutes } from "./apiRoutes";
import type { PageQuery, PageResp } from "./page";
import type { WorkUnitSummary } from "./adminWorkUnit";
import type { AdminJobTiming, WorkerQueuePage, WorkerQueuePeriod } from "./adminJobTiming";

export const diagnosticJobStatuses = [
  "queued",
  "running",
  "cancelling",
  "restarting",
  "succeeded",
  "partial_failed",
  "failed",
  "cancelled",
] as const;
export type DiagnosticJobStatus = (typeof diagnosticJobStatuses)[number];
export const diagnosticJobKinds = ["parquet", "aggregate", "video"] as const;
export type DiagnosticJobKind = (typeof diagnosticJobKinds)[number];
export type DiagnosticJobAction = "cancel" | "restart" | "resume";
export type AdminDiagnosticJob = AdminJobTiming & {
  uuid: string;
  kind: DiagnosticJobKind;
  status: DiagnosticJobStatus;
  dataset_uuid: string;
  dataset_display_name: string;
  // 강제 재계산 회차다. 대기·실행·재시작 중에만 true 로 남는다. parquet 잡이 이어 요청한 video
  // 잡도 true 다. video 잡은 현재 결과가 있어도 영상을 다시 디코드한다.
  reset: boolean;
  requested_count: number;
  // 실행 중인 video 작업은 서버가 구간 합계로 실시간 값을 준다.
  completed_count: number;
  failed_count: number;
  units: WorkUnitSummary;
  modified_at: string;
  // 이번 회차를 요청한 시각이다. 이어서 실행도 새 요청 회차다.
  queued_at: string;
};
export type AdminDiagnosticJobDetail = AdminDiagnosticJob & {
  reason: string | null;
  // video 작업은 여러 Worker 가 구간을 나눠 처리한다. 그래서 커서·점유 횟수·보고 시각은 구간마다
  // 있다. video 작업 행의 세 값은 null 이다. parquet·aggregate 작업은 Worker 하나가 처리한다. 서버는
  // 이 작업 행에 세 값을 채운다.
  progress_cursor: number | null;
  claim_count: number | null;
  last_heartbeat_at: string | null;
  // parquet·aggregate 작업을 점유한 Worker 다. video 작업은 구간마다 담당 Worker 가 있다. 그래서 이
  // 값은 null 이다.
  worker_id: string | null;
  // 서버가 계산한 점유 만료 시각이다. 점유가 없으면 null 이다.
  lease_expires_at: string | null;
  can_cancel: boolean;
  can_restart: boolean;
  can_resume: boolean;
  created_at: string;
};
// video 작업의 구간 한 행이다. 구간은 대상 에피소드 묶음이다. Worker 하나가 구간 하나를 점유한다.
export const diagnosticJobWindowStatuses = [
  "queued",
  "running",
  "done",
  "failed",
  "cancelled",
] as const;
export type DiagnosticJobWindowStatus = (typeof diagnosticJobWindowStatuses)[number];
export type AdminDiagnosticJobWindow = {
  uuid: string;
  // 1 부터 시작하는 할당 순서다.
  ordinal: number;
  status: DiagnosticJobWindowStatus;
  // 할당할 때 센 대상 에피소드 수다.
  target_count: number;
  // 이미 결과가 있어 건너뛴 에피소드를 포함한다.
  completed_count: number;
  failed_count: number;
  claim_count: number;
  claim_generation: number;
  // 마지막으로 점유한 Worker 다. 한 번도 점유하지 않았으면 null 이다.
  worker_id: string | null;
  // 서버 판정이다. 그 Worker 가 지금 이 구간과 세대를 보고하면 true 다.
  worker_reporting: boolean;
  last_heartbeat_at: string | null;
  reason: string | null;
  started_at: string | null;
  finished_at: string | null;
};
export type DiagnosticWindowEpisodeState = "done" | "failed" | "pending";
export type AdminDiagnosticWindowEpisode = {
  episode_uuid: string;
  name: string;
  state: DiagnosticWindowEpisodeState;
};
// 진단 큐의 처리량 요약이다. 판정과 순서는 서버가 정한다.
export type AdminDiagnosticCapacity = {
  observed_at: string;
  non_video_pending: number;
  // lease 가 살아 있는 실행 구간 수와 클러스터 상한(video_max_windows)이다.
  video_windows: { live: number; limit: number };
  // 진단 큐를 처리하는 online Worker와 현재 작업을 점유한 수다.
  workers: { online: number; busy: number };
  // 다음에 구간을 받을 video 작업이다. 요청 시각(queued_at) 순서로 앞 5개다.
  next: AdminDiagnosticCapacityNext[];
};
export type AdminDiagnosticCapacityNext = {
  job_uuid: string;
  dataset_display_name: string;
  queued_at: string;
  running_windows: number;
  can_open_detail: boolean;
  has_reclaimable_windows: boolean;
  // true 면 새 구간을 더 만들지 않는다. 반납된 구간만 다시 받는다.
  total_final: boolean;
};
export type AdminDiagnosticJobQuery = PageQuery & {
  period?: WorkerQueuePeriod;
  status?: readonly DiagnosticJobStatus[];
  kind?: DiagnosticJobKind;
};
export async function listAdminDiagnosticJobs(
  query: AdminDiagnosticJobQuery,
  options: { signal?: AbortSignal } = {},
): Promise<WorkerQueuePage<AdminDiagnosticJob> & { capacity: AdminDiagnosticCapacity }> {
  return (
    await apiClient.get<
      WorkerQueuePage<AdminDiagnosticJob> & { capacity: AdminDiagnosticCapacity }
    >(apiRoutes.admin.diagnosticJobs, {
      params: query,
      signal: options.signal,
    })
  ).data;
}
export async function changeDiagnosticJob(
  uuid: string,
  action: DiagnosticJobAction,
): Promise<void> {
  await apiClient.post(apiRoutes.admin.diagnosticJobAction(uuid, action));
}

export async function getAdminDiagnosticJob(uuid: string): Promise<AdminDiagnosticJobDetail> {
  return (await apiClient.get<AdminDiagnosticJobDetail>(apiRoutes.admin.diagnosticJob(uuid))).data;
}

/** 진단 큐의 처리량 요약을 조회한다. */
export async function getAdminDiagnosticCapacity(): Promise<AdminDiagnosticCapacity> {
  return (await apiClient.get<AdminDiagnosticCapacity>(apiRoutes.admin.diagnosticJobCapacity)).data;
}

/** video 작업의 구간을 할당 순서로 한 페이지씩 조회한다. */
export async function listDiagnosticJobWindows(
  uuid: string,
  query: PageQuery,
  options: { signal?: AbortSignal } = {},
): Promise<PageResp<AdminDiagnosticJobWindow>> {
  return (
    await apiClient.get<PageResp<AdminDiagnosticJobWindow>>(
      apiRoutes.admin.diagnosticJobWindows(uuid),
      { params: query, signal: options.signal },
    )
  ).data;
}

/**
 * 구간 하나의 대상 에피소드를 조회한다. 응답은 구간 크기(최대 100개)를 넘지 않는다. 그래서
 * 페이지를 나누지 않는다. 작업을 다시 시작하면 서버가 구간을 지운다. 그 뒤에는 404 다.
 */
export async function listDiagnosticJobWindowEpisodes(
  uuid: string,
  windowUuid: string,
): Promise<AdminDiagnosticWindowEpisode[]> {
  return (
    await apiClient.get<{ items: AdminDiagnosticWindowEpisode[] }>(
      apiRoutes.admin.diagnosticJobWindowEpisodes(uuid, windowUuid),
    )
  ).data.items;
}

/**
 * 진단 잡을 Dataset 마다 요청한다. 한 번에 1~100개다.
 *
 * `reset` 은 강제 재계산이다. Dataset 마다 제외하지 않은 INVALID 판정을 PENDING 으로 되돌리고
 * parquet 부터 다시 진단한다. 이어지는 video 진단은 캐시 없이 다시 디코드한다. 기존 결과는 새 결과가
 * 올 때까지 남고, 사람의 기록(결함 수용·스냅샷·코멘트·리뷰 판정·검토 완료·후보 확인)은 바꾸지 않는다.
 */
export async function createDiagnosticJobs(
  datasetUuids: readonly string[],
  reset: boolean,
): Promise<void> {
  await apiClient.post(apiRoutes.admin.diagnosticJobs, {
    dataset_uuids: datasetUuids,
    reset,
  });
}
